import { z } from "zod";
import { DEFAULT_EXPENSE_CATEGORIES } from "../expenses/defaults.js";
/**
 * Anlamsal kayıt anahtarları — diğer modüller muhasebe kaydı isterken HESAP KODU DEĞİL bu anahtarları kullanır
 * (AGENTS.md §3.15). Hesap, şirketin `account_mappings` tablosundan çözülür; bulunamazsa kayıt oluşturulmaz,
 * "eşleştirme bekleyenler" kuyruğuna düşer.
 *
 * Yazım: `ANAHTAR` ya da `ANAHTAR:parametre` (ör. `CASH:<kasa kimliği>`, `SALES_REVENUE:FUEL_DIESEL`, `VAT_OUTPUT:VAT_STANDARD`).
 * Yeni anahtar eklerken: buraya + `docs/domain/accounting.md` tablosuna + gerekiyorsa DEFAULT_ACCOUNT_MAPPINGS'e.
 */
export const POSTING_KEYS = {
    CASH: { label: "Kasa", param: "Kasa kimliği", module: "Kasa" },
    BANK: { label: "Banka hesabı", param: "Banka hesabı kimliği", module: "Banka" },
    POS_RECEIVABLE: {
        label: "POS alacakları (kredi kartı)",
        param: "POS terminali kimliği",
        module: "Banka / POS",
    },
    CUSTOMER_RECEIVABLE: { label: "Alıcılar", param: null, module: "Cari" },
    SUPPLIER_PAYABLE: { label: "Satıcılar", param: null, module: "Cari" },
    ADVANCES_RECEIVED: { label: "Alınan sipariş avansları", param: null, module: "Cari" },
    ADVANCES_GIVEN: { label: "Verilen sipariş avansları", param: null, module: "Cari" },
    DOUBTFUL_RECEIVABLE: { label: "Şüpheli ticari alacaklar", param: null, module: "Cari" },
    DOUBTFUL_PROVISION: {
        label: "Şüpheli ticari alacaklar karşılığı",
        param: null,
        module: "Çek / senet",
    },
    CHEQUE_PORTFOLIO: { label: "Portföydeki çekler", param: null, module: "Çek / senet" },
    CHEQUE_IN_COLLECTION: { label: "Tahsildeki çekler", param: null, module: "Çek / senet" },
    CHEQUE_AS_COLLATERAL: { label: "Teminattaki çekler", param: null, module: "Çek / senet" },
    CHEQUES_ISSUED: { label: "Verilen çekler ve ödeme emirleri", param: null, module: "Çek / senet" },
    NOTES_RECEIVABLE: { label: "Alacak senetleri", param: null, module: "Çek / senet" },
    NOTES_PAYABLE: { label: "Borç senetleri", param: null, module: "Çek / senet" },
    SALES_REVENUE: { label: "Satış geliri", param: "Ürünün muhasebe grubu", module: "Satış / fatura" },
    SALES_RETURNS: { label: "Satıştan iadeler", param: "Ürünün muhasebe grubu", module: "Satış / fatura" },
    SALES_DISCOUNTS: { label: "Satış iskontoları", param: "Ürünün muhasebe grubu", module: "Satış / fatura" },
    VAT_OUTPUT: { label: "Hesaplanan KDV", param: "KDV oran kodu", module: "Satış / fatura" },
    VAT_INPUT: { label: "İndirilecek KDV", param: "KDV oran kodu", module: "Alış / fatura" },
    TAXES_PAYABLE: { label: "Ödenecek vergi ve fonlar", param: null, module: "Vergi" },
    VAT_WITHHELD_PAYABLE: {
        label: "Sorumlu sıfatıyla ödenecek KDV (tevkifat)",
        param: null,
        module: "Alış / fatura",
    },
    INVENTORY: { label: "Ticari mallar (stok)", param: "Ürünün muhasebe grubu", module: "Stok" },
    COGS: { label: "Satılan ticari mal maliyeti", param: "Ürünün muhasebe grubu", module: "Stok" },
    NORMAL_LOSS: { label: "Normal fire", param: "Ürünün muhasebe grubu", module: "Akaryakıt / stok" },
    ABNORMAL_LOSS: { label: "Olağandışı kayıp", param: "Ürünün muhasebe grubu", module: "Akaryakıt / stok" },
    SHIFT_SHORTAGE: { label: "Vardiya kasa açığı", param: null, module: "Vardiya" },
    SHIFT_SURPLUS: { label: "Vardiya kasa fazlası", param: null, module: "Vardiya" },
    STOCK_COUNT_SHORTAGE: {
        label: "Sayım ve tesellüm noksanları (stok)",
        param: "Ürünün muhasebe grubu",
        module: "Stok",
    },
    STOCK_COUNT_SURPLUS: {
        label: "Sayım ve tesellüm fazlaları (stok)",
        param: "Ürünün muhasebe grubu",
        module: "Stok",
    },
    // Prompt 17 (ADR 0017 §24, #68): kazanılan puan gider + yükümlülük; harcama yükümlülüğü kapatır.
    LOYALTY_LIABILITY: { label: "Sadakat puanı yükümlülüğü", param: null, module: "Sadakat" },
    LOYALTY_EXPENSE: { label: "Sadakat puanı gideri", param: null, module: "Sadakat" },
    // Yıkama (Prompt 18, ADR 0018 §18): kimyasal tüketimi ve makinenin KENDİ tahsilatı (jeton / nakit kutusu / kart okuyucu).
    WASH_CONSUMPTION: { label: "Yıkama kimyasal tüketimi (hizmet maliyeti)", param: null, module: "Yıkama" },
    WASH_MACHINE_COLLECTION: {
        label: "Yıkama makinesi tahsilatları",
        param: "Ödeme türü (CASH / CARD)",
        module: "Yıkama",
    },
    PERSONNEL_RECEIVABLE: { label: "Personelden alacaklar", param: null, module: "Vardiya / personel" },
    TENDER_RECEIVABLE: {
        label: "Diğer tahsilat araçlarından alacaklar (TTS / mobil ödeme / kupon / puan)",
        param: "Tahsilat türü",
        module: "Vardiya",
    },
    PERSONNEL_ADVANCE: { label: "Personel avansları", param: null, module: "Personel" },
    BANK_COMMISSION: { label: "Banka ve POS komisyonları", param: null, module: "Banka / POS" },
    FINANCE_EXPENSE: { label: "Finansman giderleri", param: null, module: "Banka" },
    INTEREST_INCOME: { label: "Faiz gelirleri", param: null, module: "Banka" },
    BANK_LOAN: { label: "Banka kredileri", param: "Kredi kimliği", module: "Banka" },
    GUARANTEE_MEMO_DEBIT: {
        label: "Teminat nazım hesabı (borçlu)",
        param: "Yön (GIVEN / RECEIVED)",
        module: "Teminat",
    },
    GUARANTEE_MEMO_CREDIT: {
        label: "Teminat nazım hesabı (alacaklı)",
        param: "Yön (GIVEN / RECEIVED)",
        module: "Teminat",
    },
    EXPENSE: { label: "Gider", param: "Gider kategorisi", module: "Gider" },
    CREDIT_CARD_PAYABLE: {
        label: "Şirket kredi kartı borcu",
        param: "Kart kimliği",
        module: "Gider",
    },
    OTHER_INCOME: { label: "Diğer gelirler", param: "Gelir kategorisi", module: "Gelir" },
};
export const POSTING_KEY_NAMES = Object.keys(POSTING_KEYS);
export const postingKeyNameSchema = z.enum(POSTING_KEY_NAMES);
/** `ANAHTAR` ya da `ANAHTAR:parametre`. Parametre: kimlik ya da kod (boşluksuz, en çok 80 karakter). */
export const postingKeyRefSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{1,59}(:[A-Za-z0-9._-]{1,80})?$/, "Kayıt anahtarı ANAHTAR ya da ANAHTAR:parametre biçiminde olmalıdır")
    .refine((value) => (value.split(":")[0] ?? "") in POSTING_KEYS, "Tanımsız kayıt anahtarı")
    .describe("Anlamsal kayıt anahtarı. Örn. CASH:0197…, SALES_REVENUE:FUEL_DIESEL");
export function parsePostingKeyRef(ref) {
    const separator = ref.indexOf(":");
    return separator < 0
        ? { key: ref, param: null }
        : { key: ref.slice(0, separator), param: ref.slice(separator + 1) };
}
/**
 * Varsayılan eşleştirmeler (THP_TR şablonuna göre). Şirket oluşturulurken kopyalanır; tamamı şirket bazında
 * değiştirilebilir. MALİ MÜŞAVİR ONAYINA TABİDİR (docs/domain/regulatory-open-questions.md).
 * Kasa, banka ve POS için genel eşleştirme başlangıç içindir; ilgili modüller (Prompt 08) her kasa/banka hesabı için
 * alt hesap açıp `CASH:<kimlik>` biçiminde özel eşleştirme ekler.
 */
export const DEFAULT_ACCOUNT_MAPPINGS = [
    { key: "CASH", accountCode: "100" },
    { key: "CHEQUE_PORTFOLIO", accountCode: "101.01" },
    { key: "CHEQUE_IN_COLLECTION", accountCode: "101.02" },
    { key: "CHEQUE_AS_COLLATERAL", accountCode: "101.03" },
    { key: "BANK", accountCode: "102" },
    { key: "CHEQUES_ISSUED", accountCode: "103" },
    { key: "POS_RECEIVABLE", accountCode: "108.01" },
    { key: "CUSTOMER_RECEIVABLE", accountCode: "120" },
    { key: "NOTES_RECEIVABLE", accountCode: "121" },
    { key: "DOUBTFUL_RECEIVABLE", accountCode: "128" },
    { key: "DOUBTFUL_PROVISION", accountCode: "129" },
    { key: "PERSONNEL_RECEIVABLE", accountCode: "135" },
    // TTS / mobil ödeme / kupon / puan tahsilatları — THP 136 "Diğer çeşitli alacaklar". MEVZUAT AÇIK SORUSU:
    // uygulamada 108 alt hesabı da kullanılıyor; "doğrulanmalı" (docs/domain/regulatory-open-questions.md).
    { key: "TENDER_RECEIVABLE", accountCode: "136" },
    // Sadakat puanı (Prompt 17): 379 Diğer Borç ve Gider Karşılıkları / 760 Pazarlama Giderleri — doğrulanmalı (#68).
    { key: "LOYALTY_LIABILITY", accountCode: "379" },
    { key: "LOYALTY_EXPENSE", accountCode: "760" },
    { key: "INVENTORY", accountCode: "153.99" },
    { key: "INVENTORY", businessUnit: "FUEL", accountCode: "153.01" },
    { key: "INVENTORY", businessUnit: "LPG", accountCode: "153.02" },
    { key: "INVENTORY", businessUnit: "MARKET", accountCode: "153.03" },
    { key: "INVENTORY", businessUnit: "CAFE", accountCode: "153.05" },
    // Yıkama kimyasalı sarf malzemesidir (THP 150 İlk Madde ve Malzeme) — doğrulanmalı (#71).
    { key: "INVENTORY", businessUnit: "WASH", accountCode: "150" },
    // Makinenin kendi tahsilatı: nakit kutusu boşaltılana dek 108.02, kart okuyucusu 108.01 POS alacakları (#72).
    { key: "WASH_MACHINE_COLLECTION", param: "CASH", accountCode: "108.02" },
    { key: "WASH_MACHINE_COLLECTION", param: "CARD", accountCode: "108.01" },
    { key: "ADVANCES_GIVEN", accountCode: "159" },
    { key: "VAT_INPUT", param: "VAT_STANDARD", accountCode: "191.01" },
    { key: "VAT_INPUT", param: "VAT_REDUCED", accountCode: "191.02" },
    { key: "VAT_INPUT", param: "VAT_LOW", accountCode: "191.03" },
    { key: "PERSONNEL_ADVANCE", accountCode: "196" },
    { key: "SHIFT_SHORTAGE", accountCode: "197" },
    // Market sayım farkı (ADR 0016 §13). 197/397'nin dönem sonu kapanışı mali müşavir kararıdır (mevzuat sorusu #63).
    { key: "STOCK_COUNT_SHORTAGE", accountCode: "197" },
    { key: "BANK_LOAN", accountCode: "300" },
    { key: "SUPPLIER_PAYABLE", accountCode: "320" },
    { key: "NOTES_PAYABLE", accountCode: "321" },
    { key: "ADVANCES_RECEIVED", accountCode: "340" },
    { key: "TAXES_PAYABLE", accountCode: "360" },
    { key: "VAT_WITHHELD_PAYABLE", accountCode: "360" },
    { key: "VAT_OUTPUT", param: "VAT_STANDARD", accountCode: "391.01" },
    { key: "VAT_OUTPUT", param: "VAT_REDUCED", accountCode: "391.02" },
    { key: "VAT_OUTPUT", param: "VAT_LOW", accountCode: "391.03" },
    { key: "SHIFT_SURPLUS", accountCode: "397" },
    { key: "STOCK_COUNT_SURPLUS", accountCode: "397" },
    { key: "SALES_REVENUE", accountCode: "600.99" },
    { key: "SALES_REVENUE", businessUnit: "FUEL", accountCode: "600.01" },
    { key: "SALES_REVENUE", businessUnit: "LPG", accountCode: "600.02" },
    { key: "SALES_REVENUE", businessUnit: "MARKET", accountCode: "600.03" },
    { key: "SALES_REVENUE", businessUnit: "WASH", accountCode: "600.04" },
    { key: "SALES_REVENUE", businessUnit: "EV", accountCode: "600.05" },
    { key: "SALES_REVENUE", businessUnit: "CAFE", accountCode: "600.06" },
    { key: "SALES_REVENUE", businessUnit: "RENTAL", accountCode: "600.07" },
    { key: "SALES_RETURNS", accountCode: "610" },
    { key: "SALES_DISCOUNTS", accountCode: "611" },
    { key: "COGS", accountCode: "621.99" },
    { key: "COGS", businessUnit: "FUEL", accountCode: "621.01" },
    { key: "COGS", businessUnit: "LPG", accountCode: "621.02" },
    { key: "COGS", businessUnit: "MARKET", accountCode: "621.03" },
    { key: "COGS", businessUnit: "CAFE", accountCode: "621.05" },
    // Yıkama kimyasal tüketimi: 622 Satılan Hizmet Maliyeti (7/A kullanan şirkette 740 — mali müşavir kararı, #71).
    { key: "WASH_CONSUMPTION", accountCode: "622" },
    { key: "INTEREST_INCOME", accountCode: "642" },
    { key: "OTHER_INCOME", accountCode: "649" },
    { key: "ABNORMAL_LOSS", accountCode: "689" },
    { key: "NORMAL_LOSS", accountCode: "770.01" },
    { key: "EXPENSE", accountCode: "770.99" },
    // Varsayılan gider kategorilerinin `EXPENSE:{kod}` eşleştirmeleri. Kategori listesi TEK yerde
    // (`expenses/defaults.ts`) durur; kullanıcının açtığı yeni kategori eşleştirmesizdir ve parametresiz
    // `EXPENSE` (770.99) eşleştirmesine düşer — hesap planından ayrı bir hesaba bağlanabilir.
    ...DEFAULT_EXPENSE_CATEGORIES.map((category) => ({
        key: "EXPENSE",
        param: category.code,
        accountCode: category.accountCode,
    })),
    // Şirket kredi kartı borcu (THP 309 — mevzuat açık sorusu). Karta özel alt hesap açılırsa
    // `CREDIT_CARD_PAYABLE:<kart kimliği>` eşleştirmesi eklenir; yoksa bu parametresiz eşleştirmeye düşer.
    { key: "CREDIT_CARD_PAYABLE", accountCode: "309" },
    { key: "BANK_COMMISSION", accountCode: "780.01" },
    { key: "FINANCE_EXPENSE", accountCode: "780.99" },
    { key: "GUARANTEE_MEMO_DEBIT", param: "GIVEN", accountCode: "900" },
    { key: "GUARANTEE_MEMO_CREDIT", param: "GIVEN", accountCode: "901" },
    { key: "GUARANTEE_MEMO_DEBIT", param: "RECEIVED", accountCode: "902" },
    { key: "GUARANTEE_MEMO_CREDIT", param: "RECEIVED", accountCode: "903" },
];
//# sourceMappingURL=posting-keys.js.map